import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import type { ChargingStation, Connector } from '../components/infrastructure/chargingStation/chargingStation.types';
import type { Grid } from '../features/infrastructure/grid/grid.types';
import type { Group } from '../features/infrastructure/group/group.types';

// Station control payloads
export interface ResetStationPayload {
    chargingPointId: string;
    resetType: 'Soft' | 'Hard';
}

export interface UpdatePowerLimitPayload {
    chargingStationId: string;
    connectorId: number;
    powerLimit: string;
}

export interface ToggleManualControlPayload {
    chargingStationId: string;
    connectorId: number;
}

export interface RemoteStartStopPayload {
    action: 'start' | 'stop';
    chargingStationId: string;
    connectorId: number;
}

// Group payloads
export interface CreateGroupPayload {
    groupName: string;
    stationId: string[];
}

export interface EditGroupPayload {
    groupId: number;
    groupName?: string;
    addStationsId?: string[];
    removeStationsId?: string[];
}

export const stationApi = createApi({
    reducerPath: 'stationApi',
    baseQuery,
    tagTypes: ['Stations', 'Grids', 'Groups'],
    keepUnusedDataFor: 60,
    endpoints: (builder) => ({
        getStations: builder.query<ChargingStation[], void>({
            query: () => 'station-service/stations',
            providesTags: (result) =>
                result
                    ? [
                          { type: 'Stations' as const, id: 'LIST' },
                          ...result.map((s) => ({ type: 'Stations' as const, id: s.chargingStationId })),
                      ]
                    : [{ type: 'Stations' as const, id: 'LIST' }],
        }),

        getStation: builder.query<ChargingStation, string>({
            query: (stationId) => `station-service/stations/${stationId}`,
            providesTags: (_result, _error, id) => [{ type: 'Stations' as const, id }],
        }),

        getGrids: builder.query<Grid[], void>({
            query: () => 'station-service/grid',
            providesTags: () => [{ type: 'Grids' as const, id: 'LIST' }],
        }),

        getGroups: builder.query<Group[], void>({
            query: () => 'station-service/charging-groups',
            providesTags: (result) =>
                result
                    ? [
                          { type: 'Groups' as const, id: 'LIST' },
                          ...result.map((g) => ({ type: 'Groups' as const, id: g.id })),
                      ]
                    : [{ type: 'Groups' as const, id: 'LIST' }],
        }),

        createGroup: builder.mutation<Group, CreateGroupPayload>({
            query: (payload) => ({
                url: 'station-service/charging-groups',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: [
                { type: 'Stations', id: 'LIST' },
                { type: 'Groups', id: 'LIST' },
            ],
        }),

        editGroup: builder.mutation<Group, EditGroupPayload>({
            query: ({ groupId, ...body }) => ({
                url: `station-service/charging-groups/${groupId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: [
                { type: 'Stations', id: 'LIST' },
                { type: 'Groups', id: 'LIST' },
            ],
        }),

        deleteGroup: builder.mutation<void, number>({
            query: (groupId) => ({
                url: `station-service/charging-groups/${groupId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                { type: 'Stations', id: 'LIST' },
                { type: 'Groups', id: 'LIST' },
            ],
        }),

        resetStation: builder.mutation<void, ResetStationPayload>({
            query: ({ chargingPointId, resetType }) => ({
                url: 'station-service/stations/reset',
                method: 'POST',
                body: { chargingPointId, resetType },
            }),
            invalidatesTags: (_result, _error, { chargingPointId }) => [{ type: 'Stations', id: chargingPointId }],
        }),

        updatePowerLimit: builder.mutation<void, UpdatePowerLimitPayload>({
            query: ({ chargingStationId, connectorId, powerLimit }) => ({
                url: 'station-service/stations/update-power-limit',
                method: 'POST',
                body: { chargingStationId, connectorId, powerLimit },
            }),
            invalidatesTags: (_result, _error, { chargingStationId }) => [{ type: 'Stations', id: chargingStationId }],
        }),

        toggleManualControl: builder.mutation<Connector['manualControl'], ToggleManualControlPayload>({
            query: ({ chargingStationId, connectorId }) => ({
                url: 'station-service/stations/toggle-manuel-control',
                method: 'POST',
                body: { chargingStationId, connectorId },
            }),
            invalidatesTags: (_result, _error, { chargingStationId }) => [{ type: 'Stations', id: chargingStationId }],
        }),

        remoteStartStop: builder.mutation<{ message: string }, RemoteStartStopPayload>({
            query: ({ action, chargingStationId, connectorId }) => ({
                url: `station-service/remote/${action}`,
                method: 'POST',
                body: { chargingStationId, connectorId },
            }),
            invalidatesTags: (_result, _error, { chargingStationId }) => [{ type: 'Stations', id: chargingStationId }],
        }),
    }),
});

export const {
    useGetStationsQuery,
    useGetStationQuery,
    useGetGridsQuery,
    useGetGroupsQuery,
    useCreateGroupMutation,
    useEditGroupMutation,
    useDeleteGroupMutation,
    useResetStationMutation,
    useUpdatePowerLimitMutation,
    useToggleManualControlMutation,
    useRemoteStartStopMutation,
} = stationApi;
